import { DownOutlined } from '@ant-design/icons';
import { Avatar, Card, Col, Divider, Flex, Row, Space } from 'antd';
import NoImage from 'assets/no-image.png';
import Accordion from 'components/Accordion';
import CustomFileList from 'components/CustomFileList';
import CustomLoader from 'components/CustomLoader';
import TagComponent from 'components/Tags';
import TaskInfo from 'components/TaskInfo';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import usePreHarvestQuery from 'screens/awd-dashboard/hooks/queries/usePreHarvestQuery';
import { SelectTaskData } from 'screens/sales-group-progress';
import { STATUS_MAP, StatusMapValue } from 'utils/constants';
import { getStatusValues, renderDate, validateImageURL } from 'utils/helpers';

const PreHarvestContent = ({ data }: { data: SelectTaskData }) => {
  const { task } = { ...data };
  const status = getStatusValues(task?.status) as StatusMapValue;
  const isUpcoming = status.text === STATUS_MAP.UPCOMING.text;

  const { t } = useTranslation();

  const { data: queryData, isLoading } = usePreHarvestQuery(
    task?.id,
    !isUpcoming,
  );

  const preHarvestData = !isUpcoming
    ? queryData?.data?.preHarvestMeetingDto
    : null;

  const photoList = useMemo(
    () => ({
      meetingPhotoList:
        preHarvestData?.images
          ?.filter((img) => img.tag === 'PRE_HARVEST_MEETING')
          ?.map((img) => img.url) ?? [],
      paddyPhotoList:
        preHarvestData?.images
          ?.filter((img) => img.tag === 'PRE_HARVEST_PADDY_FIELD')
          ?.map((img) => img.url) ?? [],
    }),
    [preHarvestData],
  );

  return (
    <>
      {isLoading && (
        <div className="loader-container">
          <CustomLoader size="extra-large" />
        </div>
      )}
      <Space direction="vertical" size={24}>
        {/* Task Information */}
        <Card>
          <Row gutter={[0, 24]}>
            <Col span={12}>
              <TaskInfo header={t('dashboard.taskStatus')}>
                <TagComponent color={status.color} label={status.text} />
              </TaskInfo>
            </Col>
            <Col span={12}>
              <TaskInfo header={t('dashboard.taskCompletionDate')}>
                {renderDate(task?.inputDate)}
              </TaskInfo>
            </Col>
            <Col span={12}>
              <TaskInfo header={t('dashboard.preHarvestMeeting.meetingDate')}>
                {renderDate(preHarvestData?.meetingDate)}
              </TaskInfo>
            </Col>
            <Col span={12}>
              <TaskInfo
                header={t('dashboard.preHarvestMeeting.ehdConfirmation')}
              >
                {preHarvestData?.estimatedHarvestDateStatus}
              </TaskInfo>
            </Col>
            <Col span={24}>
              <TaskInfo
                header={t('dashboard.preHarvestMeeting.meetingSummary')}
              >
                {preHarvestData?.meetingSummary}
              </TaskInfo>
            </Col>

            <Divider style={{ padding: 0, margin: 0 }} />

            <Col span={24}>
              <TaskInfo header={t('dashboard.preHarvestMeeting.meetingPhoto')}>
                {!photoList.meetingPhotoList.length && '-'}
                <CustomFileList images={photoList.meetingPhotoList} />
              </TaskInfo>
            </Col>
            <Col span={24}>
              <TaskInfo header={t('dashboard.preHarvestMeeting.paddyPhoto')}>
                {!photoList.paddyPhotoList.length && '-'}
                <CustomFileList images={photoList.paddyPhotoList} />
              </TaskInfo>
            </Col>
          </Row>
        </Card>

        {/* Attended Farmer Accordion */}
        <Accordion
          title={t('dashboard.preHarvestMeeting.attendedFarmers')}
          style={{ background: 'white' }}
          expandIcon={({ isActive }) => (
            <Flex justify="between" align="center" gap={8}>
              <span className="text-base">
                {preHarvestData?.participants?.length ?? 0}
              </span>
              <DownOutlined rotate={isActive ? 180 : 0} />
            </Flex>
          )}
        >
          <Row gutter={[12, 24]}>
            {!preHarvestData && (
              <Col className="text-secondary">{t('common.noData')}</Col>
            )}
            {preHarvestData?.participants?.map((participant, index) => (
              <Col key={index + participant?.farmer?.id} span={12}>
                <Flex align="center" gap={8}>
                  <Avatar
                    src={
                      validateImageURL(participant?.farmer?.profileImage)
                        ? participant?.farmer?.profileImage
                        : NoImage
                    }
                    style={{ minWidth: '32px' }}
                  />
                  <span className="text-base text-one-line">
                    {participant?.farmer?.name}
                  </span>
                </Flex>
              </Col>
            ))}
          </Row>
        </Accordion>

        {/* Estimated Harvest Date Changes */}
        <Card>
          <Row gutter={[0, 24]}>
            <Col span={12}>
              <TaskInfo header={t('common.task.estimatedHarvestDate')}>
                {renderDate(preHarvestData?.originalEstimatedHarvestDate)}
              </TaskInfo>
            </Col>
            <Col span={12}>
              <TaskInfo
                header={t(
                  'dashboard.preHarvestMeeting.newEstimatedHarvestDate',
                )}
              >
                {renderDate(preHarvestData?.updatedEstimatedHarvestDate)}
              </TaskInfo>
            </Col>
            <Col span={24}>
              <TaskInfo header={t('common.reason')}>
                {preHarvestData?.reason}
              </TaskInfo>
            </Col>
            <Col span={24}>
              <TaskInfo header={t('common.reasonExplanation')}>
                {preHarvestData?.fieldCondition}
              </TaskInfo>
            </Col>
          </Row>
        </Card>
      </Space>
    </>
  );
};

export default PreHarvestContent;
